import { DialogComponent } from '@theme/dialog';

/**
 * One chooser inside the dialog - see snippets/request-choice-group.liquid.
 *
 * @typedef {object} RequestGroup
 * @property {string} title      short label for the email body line
 * @property {boolean} required  block submit when unanswered
 * @property {boolean} otherSelected  "Something else" is picked
 * @property {string} value      the answer, "" when unanswered
 * @property {HTMLInputElement | null} other  free-text input behind "Something else"
 * @property {string} errorEmpty
 * @property {string} errorOther
 */

/**
 * Product request dialog.
 *
 * Lets a shopper tell us what they would buy - a size we do not make, or a
 * product type we do not sell yet - and optionally ask a question. The dialog
 * holds one or more [data-request-group] choosers (see
 * snippets/request-choice-group.liquid); the first one is the primary choice.
 *
 * Posts two native Shopify forms over fetch so the dialog can submit both
 * without a page reload:
 *
 *   1. contact form  - always. Emails the store with the requested size, the
 *                      question, and the product it came from.
 *   2. customer form - only when the marketing consent box is ticked. Creates a
 *                      customer with accepts_marketing = true.
 *
 * Both posts are fire and forget - see #post for why the response cannot be
 * read. The thank-you is shown on tap. If this script never runs at all the
 * form still posts natively, and the reloaded page reopens on the thank-you.
 *
 * @extends DialogComponent
 */
class SizeRequestComponent extends DialogComponent {
  /** Fallback marker at the start of contact[body] so requests are greppable in the inbox. */
  static BODY_PREFIX = 'SIZE REQUEST';

  /** Fallback customer tag prefix on newsletter opt-in. */
  static TAG_PREFIX = 'size-request';

  #submitting = false;

  connectedCallback() {
    super.connectedCallback();

    // Set only when the browser submitted the form natively because this script
    // never ran on the previous page view. The request already reached Shopify,
    // so reopen on the thank-you rather than leave the shopper with nothing.
    if (this.querySelector('[data-size-request-posted]')) {
      requestAnimationFrame(() => {
        this.#setSuccess();
        this.showDialog();
      });
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();
    if (this.#submitting) return;

    const contactForm = this.#contactForm;
    if (!contactForm) return;

    const groups = this.#groups();

    // Stop on the first unanswered group that has to be answered. An optional
    // group counts once "Something else" is picked: dropping the empty text
    // silently would lose the one thing the shopper meant to tell us.
    const missing = groups.find((group) => !group.value && (group.required || group.otherSelected));
    if (missing) {
      this.#setError(missing.otherSelected ? missing.errorOther : missing.errorEmpty);
      if (missing.otherSelected) missing.other?.focus();
      return;
    }

    // The primary choice opens the email subject line and slugs the customer tag.
    const size = groups[0]?.value ?? '';

    const emailInput = this.#emailInput;
    const email = emailInput?.value.trim() ?? '';
    if (!email || emailInput?.checkValidity() === false) {
      this.#setError('Please enter a valid email address.');
      emailInput?.focus();
      return;
    }

    this.#setError(null);
    this.#setSubmitting(true);
    this.#fillContactForm(groups, size, email);

    // Thank the shopper on tap rather than on response. Waiting on the network
    // made the dialog feel broken, and the request is on its way regardless.
    this.#setSuccess();

    // The two posts are deliberately independent: a refused newsletter signup
    // must not cost us the size request, and vice versa.
    const sent = await this.#post(contactForm);

    if (this.#consent?.checked) {
      this.#subscribe(size, email);
    }

    this.#setSubmitting(false);

    // Only a request that never left the device is a failure worth reporting.
    if (!sent) this.#setFailed();
  };

  /**
   * Newsletter signup. Fire and forget - it must never affect what the shopper
   * sees, and the requested size is already captured in the email regardless.
   *
   * @param {string} size
   * @param {string} email
   */
  #subscribe(size, email) {
    const form = this.#customerForm;
    if (!form) return;

    const slug = size
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    const prefix = this.dataset.tagPrefix || SizeRequestComponent.TAG_PREFIX;

    this.#setValue('[data-size-request-customer="email"]', email);
    this.#setValue('[data-size-request-customer="tags"]', prefix + ',' + prefix + '-' + slug);

    this.#post(form);
  }

  /**
   * Reveals a group's free-text input when its "Something else" radio is picked.
   * Bound on every group, so resolve which one fired rather than assuming.
   *
   * @param {Event} event
   */
  handleSizeChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    const group = target.closest('[data-request-group]');
    const other = group?.querySelector('[data-size-request-other]');
    if (!(other instanceof HTMLInputElement)) return;

    const isOther = target.value === '__other';
    other.hidden = !isOther;
    other.required = isOther;
    if (isOther) other.focus();

    this.#setError(null);
  };

  /* ----------------------------------------------------------------- internals */

  /**
   * Every chooser in the dialog, in DOM order. The first is the primary choice.
   *
   * @returns {RequestGroup[]}
   */
  #groups() {
    return [...this.querySelectorAll('[data-request-group]')].map((group) => {
      const other = group.querySelector('[data-size-request-other]');
      const otherInput = other instanceof HTMLInputElement ? other : null;
      const checked = group.querySelector('input[type="radio"]:checked');

      const otherSelected = checked instanceof HTMLInputElement && checked.value === '__other';

      let value = '';
      if (checked instanceof HTMLInputElement) {
        // Scope the free text to its own group: two choosers each have one.
        value = otherSelected ? (otherInput?.value.trim() ?? '') : checked.value;
      }

      return {
        title: group.getAttribute('data-group-title') ?? '',
        required: group.hasAttribute('data-group-required'),
        otherSelected,
        value,
        other: otherInput,
        errorEmpty: group.getAttribute('data-error-empty') || 'Please choose an option.',
        errorOther: group.getAttribute('data-error-other') || 'Please fill this in.',
      };
    });
  }

  /** @returns {HTMLFormElement | null} */
  get #contactForm() {
    return this.querySelector('form[data-size-request-form="contact"]');
  }

  /** @returns {HTMLFormElement | null} */
  get #customerForm() {
    return this.querySelector('form[data-size-request-form="customer"]');
  }

  /** @returns {HTMLInputElement | null} */
  get #emailInput() {
    return this.querySelector('[data-size-request-email]');
  }

  /** @returns {HTMLInputElement | null} */
  get #consent() {
    return this.querySelector('[data-size-request-consent]');
  }

  /**
   * @param {RequestGroup[]} groups
   * @param {string} size primary choice
   * @param {string} email
   */
  #fillContactForm(groups, size, email) {
    const question = this.querySelector('[data-size-request-question]');
    const questionText = question instanceof HTMLTextAreaElement ? question.value.trim() : '';

    const prefix = this.dataset.bodyPrefix || SizeRequestComponent.BODY_PREFIX;

    const lines = [
      prefix + ': ' + size,
      // One line per chooser, so a second group (e.g. the size on a type
      // request) lands in the email without another template to maintain.
      ...groups.filter((group) => group.value && group.title).map((group) => group.title + ': ' + group.value),
      'Product: ' + (this.dataset.productTitle ?? ''),
      'URL: ' + (this.dataset.productUrl ?? ''),
      'Current size: ' + (this.dataset.currentSize ?? ''),
      'Newsletter opt-in: ' + (this.#consent?.checked ? 'yes' : 'no'),
    ];
    if (questionText) lines.push('', 'Question: ' + questionText);

    this.#setValue('[data-size-request-input="email"]', email);
    this.#setValue('[data-size-request-input="requested_size"]', size);
    this.#setValue('[data-size-request-input="question"]', questionText);
    this.#setValue('[data-size-request-input="product"]', this.dataset.productTitle ?? '');
    this.#setValue('[data-size-request-input="product_url"]', this.dataset.productUrl ?? '');
    this.#setValue('[data-size-request-input="body"]', lines.join('\n'));
  }

  /**
   * @param {string} selector
   * @param {string} value
   */
  #setValue(selector, value) {
    const field = this.querySelector(selector);
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) field.value = value;
  }

  /**
   * Posts a Shopify form, fire and forget.
   *
   * The response is deliberately never inspected. Shopify accepts the post and
   * then 302s to the shop's canonical domain, so from a page served on any other
   * host - a *.myshopify.com preview, a secondary domain - the final response is
   * cross-origin and unreadable. Earlier versions read `ok` / `status` / the
   * redirect URL and reported "that didn't send" for submissions Shopify had
   * already accepted and emailed. Unreadable is not the same as failed.
   *
   * `no-cors` keeps a cross-origin redirect from throwing at all, and urlencoded
   * + no custom headers keeps this a CORS-safelisted request, exactly like the
   * native form post it stands in for. `keepalive` lets it finish even if the
   * shopper closes the dialog or navigates away immediately.
   *
   * @param {HTMLFormElement} form
   * @returns {Promise<boolean>} false only if the request never left the device.
   */
  async #post(form) {
    try {
      await fetch(form.action, {
        method: 'POST',
        mode: 'no-cors',
        body: new URLSearchParams(/** @type {any} */ (new FormData(form))),
        keepalive: true,
      });

      return true;
    } catch (error) {
      // Genuinely did not send: offline, or the request was blocked outright.
      console.warn('[size-request] post failed', form.action, error);
      return false;
    }
  }

  /** @param {boolean} state */
  #setSubmitting(state) {
    this.#submitting = state;
    const button = this.querySelector('[data-size-request-submit]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = state;
      button.setAttribute('aria-busy', String(state));
    }
  }

  /** @param {string | null} message */
  #setError(message) {
    const target = this.querySelector('[data-size-request-error]');
    if (!(target instanceof HTMLElement)) return;

    target.textContent = message ?? '';
    target.hidden = !message;
  }

  #setSuccess() {
    const panel = this.querySelector('[data-size-request-panel]');
    const success = this.querySelector('[data-size-request-success]');
    if (panel instanceof HTMLElement) panel.hidden = true;
    if (success instanceof HTMLElement) {
      success.hidden = false;
      success.focus();
    }
  }

  /** The thank-you was shown optimistically, so own up if it never landed. */
  #setFailed() {
    const success = this.querySelector('[data-size-request-success]');
    const failed = this.querySelector('[data-size-request-failed]');
    if (success instanceof HTMLElement) success.hidden = true;
    if (failed instanceof HTMLElement) {
      failed.hidden = false;
      failed.focus();
    }
  }
}

if (!customElements.get('size-request-component')) {
  customElements.define('size-request-component', SizeRequestComponent);
}
